import type React from "react"
import { Fragment, useEffect, useMemo, useState } from "react"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Checkbox } from "../../components/ui/checkbox"
import { Label } from "../../components/ui/label"
import { ChevronLeft, Loader, Upload } from "lucide-react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select"
import useSWR from "swr"
import axiosConfig, { fetcher } from "../../util/axios"
import uploadToS3 from "../../util/uploadToS3"
import { Link, useLocation, useNavigate, useParams } from "react-router-dom"
import placeholder from "../../util/placeholder"
import PageSpinner from "../../components/PageSpinner"
import { DatePicker, message } from "antd"
import requestErrorHandler from "../../util/requestErrorHandler"
import { cn } from "../../lib/utils"
import dayjs from "dayjs"
import { RangePickerProps } from "antd/es/date-picker"
import { twMerge } from "tailwind-merge"

export default function ModifyVehicle() {
  const { id } = useParams();
  const location = useLocation();
  const vehicle = location?.state?.vehicle;
  const [selectedTruck, setSelectedTruck] = useState<TruckCategory>()
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([])
  const [searchZone, setSearchZone] = useState("")
  const [selectAll, setSelectAll] = useState(false)
  const [selectedZones, setSelectedZones] = useState<string[]>(vehicle?.zoneListed || [])
  const [locationProvider, setLocationProvider] = useState(vehicle?.trackingProvider || "")
  const [deviceId, setDeviceId] = useState(vehicle?.trackingDeviceId || "")
  const [driverName, setDriverName] = useState(vehicle?.driverName || "")
  const [phoneNumber, setPhoneNumber] = useState(vehicle?.driverPhone || "")
  const [isModifying, setIsModifying]= useState<boolean>(false)
  const navigate = useNavigate();

  const { data, isLoading } = useSWR<{ truckCategories: TruckCategory[] }>(
    { url: "newAdmin/truck-category-all" },
    fetcher,
  )

  const zones: Zone[]  = selectedTruck?.zones!;

  const truckCategories = useMemo(() => {
    return data?.truckCategories ?? []
  }, [data]);

  const documentTypes = [
    { name: "Vehicle License", key: "vehicleLicense", required: true },
    { name: "Hackney Permit", key: "hackneyPermit", required: true },
    { name: "Insurance", key: "insurance", required: true },
    { name: "CMR", key: "cmr", required: true },
    { name: "Customs", key: "customs", required: true },
    { name: "Proof of Ownership", key: "proofOfOwnership", required: true },
  ]

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked)
    if (checked) {
      setSelectedZones(zones?.map((zone:Zone) => zone.id))
    } else {
      setSelectedZones([])
    }
  }

  const handleSelectZone = (zoneId: string, checked: boolean) => {
    if (checked) {
      setSelectedZones([...selectedZones, zoneId])
    } else {
      setSelectedZones(selectedZones.filter((id) => id !== zoneId))
    }
  }

  const [headColor, setHeadColor] = useState(vehicle?.headColor || "")
  const [bodyColor, setBodyColor] = useState(vehicle?.bodyColor || "")
  const [regPlate, setRegPlate] = useState(vehicle?.regPlate || "")
  const [chasisNum, setChasisNum] = useState(vehicle?.chasisNum || "")

  const [isLoadingUrls, setIsLoadingUrls] = useState<any>({
    vehicleLicense: false,
    hackneyPermit: false,
    insurance: false,
    cmr: false,
    customs: false,
    proofOfOwnership: false,
  })  

  const [documentUrls, setDocumentUrls] = useState<any>({
    vehicleLicense: vehicle?.vehicleLicense || "",
    hackneyPermit: vehicle?.hackneyPermit || "",
    insurance: vehicle?.insurance || "",
    cmr: vehicle?.cmr || "",
    customs: vehicle?.customs || "",
    proofOfOwnership: vehicle?.proofOfOwnership || "",
  })

  const [documentDates, setDocumentDates] = useState<any>({
    vehicleLicense: vehicle?.vehicleLicenseIssuanceDate || "",
    hackneyPermit: vehicle?.hackneyPermitIssuanceDate || "",
    insurance: vehicle?.insuranceIssuanceDate || "",
    cmr: vehicle?.cmrIssuanceDate || "",
    customs: vehicle?.customsIssuanceDate || "",
    proofOfOwnership: vehicle?.proofOfOwnershipIssuanceDate || "",
  })

   const isNotModified = useMemo(() => {
        let isValid = true;
        selectedZones.forEach((zone) => {
            if (!vehicle?.zoneListed.includes(zone)) {
                isValid = false;
            }
        })
        return [
          vehicle?.trackingProvider === locationProvider,
          vehicle?.trackingDeviceId === deviceId,
          vehicle?.driverName === driverName,
          vehicle?.driverPhone === phoneNumber,
          vehicle?.vehicleLicense === documentUrls.vehicleLicense,
          vehicle?.hackneyPermit === documentUrls.hackneyPermit,
          vehicle?.insurance === documentUrls.insurance,
          vehicle?.cmr === documentUrls.cmr,
          vehicle?.proofOfOwnership === documentUrls.proofOfOwnership,
          vehicle?.customs === documentUrls.customs,
          vehicle?.cmrIssuanceDate === documentDates.cmr,
          vehicle?.insuranceIssuanceDate === documentDates.insurance,
          vehicle?.customsIssuanceDate === documentDates.customs,
          vehicle?.vehicleLicenseIssuanceDate === documentDates.vehicleLicense,
          vehicle?.hackneyPermitIssuanceDate === documentDates.hackneyPermit,
          vehicle?.proofOfOwnershipIssuanceDate === documentDates.proofOfOwnership,
          vehicle?.truckCategory === selectedTruck?.id,
          isValid
        ].every(valid => valid);
   }, [
    vehicle,
    documentUrls,
    selectedZones,
    documentDates,
    driverName,
    deviceId,
    selectedTruck,
    locationProvider,
    phoneNumber,
  ]);

  const updatedFields = useMemo(() => {
    let isValid = true;
    vehicle?.zoneListed.forEach((zone: any) => {
      if (!selectedZones.includes(zone)) {
        isValid = false;
      }
    });
            
    const defaultInfo = {
      trackingProvider: vehicle?.trackingProvider,
      trackingDeviceId: vehicle?.trackingDeviceId,
      driverName: vehicle?.driverName,
      driverPhone: vehicle?.driverPhone,
      vehicleLicense: vehicle?.vehicleLicense,
      hackneyPermit: vehicle?.hackneyPermit,
      insurance: vehicle?.insurance,
      cmr: vehicle?.cmr,
      proofOfOwnership: vehicle?.proofOfOwnership,
      customs: vehicle?.customs,
      cmrIssuanceDate: vehicle?.cmrIssuanceDate,
      insuranceIssuanceDate: vehicle?.insuranceIssuanceDate,
      customsIssuanceDate: vehicle?.customsIssuanceDate,
      vehicleLicenseIssuanceDate: vehicle?.vehicleLicenseIssuanceDate,
      hackneyPermitIssuanceDate: vehicle?.hackneyPermitIssuanceDate,
      proofOfOwnershipIssuanceDate: vehicle?.proofOfOwnershipIssuanceDate,
    };

    const updateInfo = {
      trackingProvider: locationProvider,
      trackingDeviceId: deviceId,
      driverName: driverName,
      driverPhone: phoneNumber,
      vehicleLicense: documentUrls?.vehicleLicense,
      hackneyPermit: documentUrls.hackneyPermit,
      insurance: documentUrls.insurance,
      cmr: documentUrls.cmr,
      proofOfOwnership: documentUrls.proofOfOwnership,
      customs: documentUrls.customs,
      cmrIssuanceDate: documentDates.cmr,
      insuranceIssuanceDate: documentDates.insurance,
      customsIssuanceDate: documentDates.customs,
      vehicleLicenseIssuanceDate: documentDates.vehicleLicense,
      hackneyPermitIssuanceDate: documentDates.hackneyPermit,
      proofOfOwnershipIssuanceDate: documentDates.proofOfOwnership,
    }

    if(!isValid) {
      const updated = diffObjects(defaultInfo, updateInfo)
      return { ...updated, zoneListed: selectedZones };
    }

    return diffObjects(defaultInfo, updateInfo)
  }, [
    vehicle,
    documentUrls,
    selectedZones,
    documentDates,
    driverName,
    deviceId,
    // selectedTruck,
    locationProvider,
    phoneNumber
  ]);

  // console.log({ updatedFields })

  function diffObjects(objA: any, objB: any) {
    const objC: Record<string, any> = {};
    const keys = new Set([...Object.keys(objA), ...Object.keys(objB)]);
    for (const k of keys) {
      if (objA[k] !== objB[k] && k in objB) {
        objC[k] = objB[k];
      }
    }
    return objC;
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, documentType: string) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0]

      try {
        setIsLoadingUrls((prev:any) => ({
          ...prev,
          [documentType]: true,
        }))        
        await uploadToS3({
          file: selectedFile,
          data: {},
          filename: selectedFile.name,
          onSuccess: (url: string) => {
            setDocumentUrls((prev:any) => ({
              ...prev,
              [documentType]: url,
            }))
            console.log(`Uploaded ${documentType} document: ${url}`)
          },
          onError: (error: any) => {
            console.error(`Upload failed for ${documentType}:`, error)
            alert(`Failed to upload ${documentType}. Please try again.`)
          },
        })
      } catch (error) {
        console.error("Upload error:", error)
        alert("Upload failed. Please try again.")
      } finally {
        setIsLoadingUrls((prev:any) => ({
          ...prev,
          [documentType]: false,
        }))
      }
    }
  }

  const handleModifyVehicle = async () => {
    setIsModifying(true);

    try {
      const payload = {
        id: id,
        ...updatedFields
      }

      const response = await axiosConfig.put("/fleet/vehicles", {
         ...payload,
      })

      if (!response.data) {
        throw new Error(`API error: ${response.status}`)
      }

      message.success("Vehicle created Successfully!")
      navigate("/vehicles")
      // Reset form or redirect
    } catch (error) {
      requestErrorHandler(error)
      console.error("Error creating vehicle:", error)
    } finally {
      setIsModifying(false);
    }
  }

  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    // Can not select days before today and today
    // return current && current < dayjs().endOf('day');
    // Can not select days before today
    return current && current < dayjs().subtract(1, 'day').endOf('day');
  };

 useEffect(() => {
    if (!truckCategories.length) return;

    setSelectedTruck(truckCategories.find((truck) => truck.id === vehicle?.truckCategory))
    
 }, [truckCategories, vehicle]);
  
 if (isLoading){
    return<PageSpinner />  
 }
  const showZoneSelect = !Object.values(documentUrls).some(doc => doc === "") && selectedTruck

  return (
    <div className="p-6">
      <div className="py-4 flex items-center">
        <Link to="/vehicles" className="text-primary mr-4">
          <ChevronLeft size={20} />
        </Link>
        <h1 className="text-xl font-bold">Modify Vehicle</h1>
      </div>

      <div className="flex flex-col md:flex-row gap-12">
        {/* Left Column - Vehicle Details */}
        <div className="flex-1">
          <div className="mb-4">
            <h2 className="text-gray-500 mb-6">Select Category and material</h2>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 md:max-w-3xl">
              {truckCategories?.map((type) => (
                <div
                  key={type.id}
                  className={`border rounded-md p-4 flex md:flex-col items-center  gap-4 cursor-pointer transition-all ${selectedTruck?.id === type.id
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                    }`}
                  onClick={() => setSelectedTruck(type)}
                >
                  <div className="size-14 mb-2 relative">
                    <img
                      src={!type.image || type.image.includes("example") ? placeholder: type.image}
                      alt={type.name}
                      className="object-contain size-full"
                    />
                  </div>
                  <span className="text-sm text-center">{type.name}</span>
                </div>
              ))}
            </div>
          </div>

          {selectedTruck && (
            <div>
              <div className="mb-8">
                <h3 className="text-gray-500 mb-4">Materials carried</h3>
                <div className="grid grid-cols-3 gap-4">
                  {selectedTruck?.materials?.map((material, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <Checkbox
                        onCheckedChange={(e) => {
                          if (e === false) {
                            const mat = [...selectedMaterials!]
                            mat?.filter((m) => m !== material.name)
                            setSelectedMaterials(mat)
                          } else {
                            setSelectedMaterials((prev) => [...prev!, material?.name])
                          }
                        }}
                        id={`material-${index}`}
                        defaultChecked
                      />
                      <Label htmlFor={`material-${index}`}>
                        {material.name}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 mb-8">
                <div className="space-y-2">
                  <Label htmlFor="head-color">
                    Head colour <span className="text-red-500">*</span>
                  </Label>
                  <Input placeholder="Black" id="head-color" value={headColor} onChange={(e) => setHeadColor(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="body-color">Body Colour</Label>
                  <Input placeholder="Blue" id="body-color" value={bodyColor} onChange={(e) => setBodyColor(e.target.value)} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 mb-8">
                <div className="space-y-2">
                  <Label htmlFor="registration-plate">
                    Registration plate <span className="text-red-500">*</span>
                  </Label>
                  <Input placeholder="abc-123" id="registration-plate" value={regPlate} onChange={(e) => setRegPlate(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="chassis-number">Chasis Number</Label>
                  <Input placeholder="123123assd123" id="chassis-number" value={chasisNum} onChange={(e) => setChasisNum(e.target.value)} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 mb-8">
                {documentTypes?.map((doc, index) => {
                  const docKey = doc.key
                  return (<Fragment key={index}>
                    <div className="space-y-2">
                      <Label>
                        {doc.name} {doc.required && <span className="text-red-500">*</span>}
                      </Label>
                      <Button
                        className={twMerge("w-full p-0 m-0 bg-blue-100 border-2 rounded-none border-dashed border-primary text-blue-500 h-12", isLoadingUrls[docKey] ? "bg-gray-200 cursor-not-allowed hover:bg-transparent" : "")}
                        type="button"
                      >
                        <input
                          type="file"
                          id={`doc-${docKey}`}
                          disabled={isLoadingUrls[docKey]}
                          onChange={(e) => handleFileUpload(e, docKey)}
                          className="hidden w-full"
                        />
                        <label
                          htmlFor={`doc-${docKey}`}
                          className={twMerge("flex items-center cursor-pointer w-full h-full justify-center", isLoadingUrls[docKey] ? "cursor-not-allowed" : "")}
                        >
                          <Upload className="w-5 h-5 mr-2" />
                          {documentUrls[docKey!]
                            ? isLoadingUrls[docKey] ? <Loader className="animate-spin"/> : "File Uploaded"
                            : isLoadingUrls[docKey] ? <Loader className="animate-spin"/> : "Upload"}
                        </label>
                      </Button>
                      {documentUrls[docKey!] && (
                        <p className="text-xs text-green-600 truncate">
                          File uploaded successfully
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`doc-${docKey}-date`}>
                        {`${doc.name} Issuance Date`} {doc.required && <span className="text-red-500">*</span>}
                      </Label>
                      <DatePicker
                        format="YYYY-MM-DD"
                        disabledDate={disabledDate}
                        value={dayjs(documentDates[docKey] ? documentDates[docKey] : undefined )}
                        onChange={(date, dateString) => {
                        setDocumentDates((prev: any) => ({
                          ...prev,
                          [docKey]: dateString,
                        }))
                      }} />
                    </div>
                  </Fragment>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Zone Availability and Tracking */}
          <div className={cn('w-full md:w-[450px]', showZoneSelect ? 'block' : 'hidden')}>
            {/* Zone Availability Section */}
            <div className="mb-8">
              <h2 className="text-gray-500 mb-6">Set zone availability</h2>

              <div className="mb-4">
                <Input
                  placeholder="Search"
                  value={searchZone}
                  onChange={(e) => setSearchZone(e.target.value)}
                  className="border-b border-t-0 border-l-0 border-r-0 rounded-none px-0 focus-visible:ring-0"
                />
              </div>

              <div className="mb-4 flex items-center">
                <Checkbox
                  id="select-all"
                  checked={selectAll}
                  onCheckedChange={(checked) => handleSelectAll(checked as boolean)}
                />
                <Label htmlFor="select-all" className="ml-2">
                  Select all
                </Label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {zones?.map((zone) => (
                  <div key={zone.id} className="flex items-start">
                    <div className="flex-1 flex border rounded-md p-3">
                      <div className="flex items-center mr-2">
                        <Checkbox
                          id={`zone-${zone.id}`}
                          checked={selectedZones.includes(zone.id)}
                          onCheckedChange={(checked) => handleSelectZone(zone.id, checked as boolean)}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium">{zone.title}</div>
                        <div className="text-xs text-gray-500">{zone.description}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Configure Tracking Section */}
            <div className={cn('mb-8', selectedZones.length > 0 ? "block" :"hidden")} >
              <h2 className=" mb-6">Configure tracking</h2>

              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-gray-500" htmlFor="location-provider " >Location Service Provider</Label>
                  <div className="relative">
                    <Select value={locationProvider} onValueChange={setLocationProvider}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select provider" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="eonsfleet">Eonsfleet</SelectItem>
                        <SelectItem value="provider2">Provider 2</SelectItem>
                        <SelectItem value="provider3">Provider 3</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-500" htmlFor="device-id">Device ID</Label>
                  <Input placeholder="xyz-321" id="device-id" value={deviceId} onChange={(e) => setDeviceId(e.target.value)} />
                </div>
              </div>
            </div>

            {/* Driver Information Section */}
            <div className={cn('mb-8', selectedZones.length > 0 ? "block" :"hidden")} >
              <h2 className=" mb-6">Driver information</h2>

              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-gray-500" htmlFor="driver-name">
                    Driver Full Name <span className="text-red-500">*</span>
                  </Label>
                  <Input placeholder="Musa" id="driver-name" value={driverName} onChange={(e) => setDriverName(e.target.value)} />
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-500" htmlFor="phone-number">
                    Phone Number <span className="text-red-500">*</span>
                  </Label>
                  <Input placeholder="09011223332" id="phone-number" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} />
                </div>
              </div>
            </div>
          </div>
      </div>


      {!isNotModified && (
        <div className="flex flex-col md:flex-row items-center justify-between my-4 md:mb-8">
            <Button onClick={() => navigate("/vehicles")} className="bg-white text-black border hover:bg-grey w-full md:w-auto">Cancel</Button>
            <Button onClick={handleModifyVehicle} className="">
                {isModifying ? <Loader /> : "Save"}
            </Button>
        </div>
      )}
    </div>
  )
}

